import React, { useState } from 'react';
import {
  Box, Tabs, Tab, Alert, CircularProgress, Typography, LinearProgress,
} from '@mui/material';
import TrainingInsightsSummary from './TrainingInsightsSummary';
import TrainingInsightsDetails from './TrainingInsightsDetails';
import type { TrainingInsights } from './types';

// Re-export so existing consumers (TrainingSessionEvaluation.tsx) keep working
// with `import TrainingInsightsTab, { TrainingInsights } from '...TrainingInsightsTab'`.
export type { TrainingInsights } from './types';

interface Props {
  insights: TrainingInsights | null | undefined;
  /** Optional "Re-run Analysis" control, rendered on the sub-tabs row
   *  (and beside the status alert when there are no insights yet). */
  reanalyzeButton?: React.ReactNode;
  /** Jump the session's conversation timeline to an ISO timestamp. */
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

/**
 * Training Insights tab — top-level container with Summary / Details
 * sub-tabs mirroring the post-call Call Insights pattern.
 */
const TrainingInsightsTab: React.FC<Props> = ({
  insights, reanalyzeButton, onNavigateToTimestamp,
}) => {
  const [tab, setTab] = useState<'summary' | 'details'>('summary');

  if (!insights) {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Alert severity="info" sx={{ flex: 1 }}>
          Training insights are not available for this session.
        </Alert>
        {reanalyzeButton}
      </Box>
    );
  }

  if (insights.status === 'failed') {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Alert severity="error" sx={{ flex: 1 }}>
          Analysis failed{insights.error ? `: ${insights.error}` : '.'} Use
          Re-run Analysis to try again.
        </Alert>
        {reanalyzeButton}
      </Box>
    );
  }

  const isPending = insights.status === 'pending';
  const dimsComplete = insights.dimensionsComplete?.length ?? 0;
  const dimsTotal = 8; // 7 analysis dimensions + overall coaching synthesis

  return (
    <Box>
      {isPending && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              AI analysis is running — {dimsComplete} of {dimsTotal} dimensions ready.
              Refresh in a moment for the rest.
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(dimsComplete / dimsTotal) * 100}
            sx={{ height: 4, borderRadius: 1 }}
          />
        </Box>
      )}

      {/* Sub-tabs and the Re-run control share one row to save vertical space. */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Summary" value="summary" />
          <Tab label="Details" value="details" />
        </Tabs>
        {reanalyzeButton}
      </Box>

      {tab === 'summary' && <TrainingInsightsSummary insights={insights} />}
      {tab === 'details' && (
        <TrainingInsightsDetails
          insights={insights}
          onNavigateToTimestamp={onNavigateToTimestamp}
        />
      )}
    </Box>
  );
};

export default TrainingInsightsTab;
